//#region Util Imports

import * as moment from 'moment'
import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import ArrayStore from 'devextreme/data/array_store'
import DataSource from 'devextreme/data/data_source'
import { dxTextBoxOptions } from 'devextreme/ui/text_box'
import { dxSelectBoxOptions } from 'devextreme/ui/select_box'
import dxForm, { dxFormOptions, dxFormSimpleItem } from 'devextreme/ui/form'
import { dxValidationGroupResult } from 'devextreme/ui/validation_group'

//#endregion

//#region Asset Imports

import { IMRBaseControlController } from '../../../common/base'
import { IIMRAddExternalDevice, IMRAddExternalDeviceOptions } from './add-external-device.interfaces'
import { IIMRPopup, IMRPopupOptions } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'
import { EventsNames, LocalizationHelper } from '../../../../helpers'
import { UserSessionService } from '../../../../services/user-session'
import { OpAvailableExternalDevicePattern } from '../../../../models/custom/op-available-external-device-pattern'
import { DeviceService } from '../../../../services/device'
import { Enums } from '../../../../models/common'
import { OpDevice, OpLocation } from 'app/models/core'
import { SourcePopover } from 'app/modules/common/select-box-popup'

//#endregion

export class AddExternalDeviceController extends IMRBaseControlController<IIMRAddExternalDevice, IMRAddExternalDeviceOptions> implements ng.IComponentController, IIMRAddExternalDevice {
	public formOptions: dxFormOptions
	public popupOptions: IMRPopupOptions
	public isLoading: boolean
	public popupId: number

	private moduleName: Enums.SimaxModuleName = Enums.SimaxModuleName.Device
	private popup: IIMRPopup
	private form: dxForm

	constructor(private userSessionService: UserSessionService, private deviceService: DeviceService, private $scope: ng.IScope) {
		super()

		this.popupId = new Date().getTime()
	}

	//#region Consts

	//#endregion

	//#region Component

	protected initialize(options: IMRAddExternalDeviceOptions): void {
		if (options != null) {
			this.setPopupOptions()
		}
	}

	//#endregion

	//#region IIMRAddExternalDevice implementation

	public show(): void {
		if (this.popup != null) {
			this.popup.show()

			if (this.form == null) {
				this.setOptions()
			}
		}
	}

	public hide(): void {
		if (this.popup != null) {
			this.popup.hide()
		}
	}

	//#endregion

	private setOptions(): void {
		this.isLoading = true
		this.getPaterns()
			.then((patterns: OpAvailableExternalDevicePattern[]) => {
				this.setFormOptions(patterns)
			})
			.always(() => {
				this.isLoading = false
			})
	}

	private setFormOptions(patterns: OpAvailableExternalDevicePattern[]): void {
		this.formOptions = {
			colCount: 2,
			labelLocation: 'top',
			items: this.getFormItems(patterns),
			onInitialized: (e: any) => {
				this.form = e.component
			},
		}
	}

	private getFormItems(patterns: OpAvailableExternalDevicePattern[]): dxFormSimpleItem[] {
		return [
			{
				dataField: 'serialNbr',
				label: { text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SerialNbr) },
				editorType: 'dxTextBox',
				isRequired: true,
				editorOptions: <dxTextBoxOptions>{},
				validationRules: [
					{
						type: 'numeric',
					},
				],
			},
			{
				dataField: 'pattern',
				editorType: 'dxSelectBox',
				label: { text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TypeAndSeries) },
				isRequired: true,
				editorOptions: <dxSelectBoxOptions>{
					displayExpr: 'name',
					valueExpr: 'id',
					items: _.map(patterns, (pattern: OpAvailableExternalDevicePattern) => {
						return { id: pattern.idFile, name: pattern.toString() }
					}),
				},
			},
		]
	}

	private setPopupOptions(): void {
		this.popupOptions = {
			innerOptions: {
				title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__ADDING, this.moduleName),
				maxWidth: 600,
				maxHeight: 500,
			},
			onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
				this.popup = e.component
			},
			onHiding: () => {
				if (this.form != null) {
					this.form.resetValues()
				}

				this.popup.hideLoading()
			},
			bottomToolbarOptions: {
				items: [
					{
						location: 'after',
						widget: 'dxButton',
						options: <DevExpress.ui.dxButtonOptions>{
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),
							hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),
							onClick: (e: any) => {
								this.onSubmit()
							},
						},
					},
					{
						location: 'after',
						widget: 'dxButton',
						options: <DevExpress.ui.dxButtonOptions>{
							text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
							hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
							onClick: (e: any) => {
								this.popup.hide()
								setTimeout(() => {
									this.form.resetValues()
								})
							},
						},
					},
				],
			},
		}
	}

	private getPaterns(): JQueryPromise<OpAvailableExternalDevicePattern[]> {
		return this.deviceService.getExternalDeviceConfigurationPattern(this.userSessionService.getDataProvider()).then((patterns: OpAvailableExternalDevicePattern[]) => {
			return patterns
		})
	}

	private onSubmit(): void {
		const validationResult: dxValidationGroupResult = this.form.validate()

		if (validationResult != null && validationResult.isValid) {
			this.popup.showLoading()

			this.openLocationSelect()
				.then((idLocation: number) => {
					const formData: { pattern: number; serialNbr: number } = this.form.option('formData')

					this.deviceService
						.saveExternalDeviceConfigurationPattern(this.userSessionService.getDataProvider(), formData.serialNbr, formData.pattern, idLocation, this.moduleName)
						.then(() => {
							if (_.isFunction(this.options.onSubmited)) {
								this.options.onSubmited()
							}

							this.popup.hide()
						})
						.always(() => {
							this.popup.hideLoading()
						})
				})
				.catch(() => {
					this.popup.hideLoading()
				})
		}
	}

	//#region Location Select

	private openLocationSelect(): JQueryPromise<number> {
		let isSaving: boolean = false
		const deferred: JQueryDeferred<number> = $.Deferred()

		const selectPopoverSource: SourcePopover = {
			popupTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SelectLocationToAssign),

			onSubmit: (idLocation: number) => {
				isSaving = true

				deferred.resolve(idLocation)
			},

			onHiding: () => {
				if (isSaving === false) [deferred.reject()]
			},

			selectPopoverSource: {
				dataTypeId: Enums.DataType.HELPER_ID_LOCATION,
				referenceTypeId: Enums.ReferenceType.IdLocation,
				selectedId: 0,
				// dataIdSourceModule: Enums.WebSimaxModule.Locations,
			},
		}

		this.$scope.$root.$broadcast(EventsNames.HIDE_LOADING_INDICATOR)
		this.$scope.$broadcast(EventsNames.OPEN_SELECT_POPOVER_POPUP + this.popupId, selectPopoverSource)

		return deferred.promise()
	}

	//#endregion
}
